import { ApplicationModuleCode } from '@/domain/application-modules/application-module.interface';
import { ApplicationModulesService } from '@/domain/application-modules/application-modules.service';
import { ProfileProvider } from '@/providers/profile/profile.provider';
import { ACCESS_INTERNAL_MODULE, IOT_SUPERVISION } from '@/providers/rights.constants';
import { FeatureCode } from '@/utils/disabled-features/disabled-features.interface';
import { DisabledFeaturesService } from '@/utils/disabled-features/disabled-features.service';
import { inject, Injectable } from '@angular/core';
import { Router, type CanActivateChildFn, type CanActivateFn } from '@angular/router';
import { combineLatest, map, of, type Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class IoTSupervisionFeatureService {
    private _router = inject(Router);
    private _profileProvider = inject(ProfileProvider);
    private _disabledFeaturesProvider = inject(DisabledFeaturesService);
    private _applicationModulesProvider = inject(ApplicationModulesService);

    goToFeature() {
        this._router.navigate(['/', 'iot-supervision']);
    }

    hasFeatureAccess$(): Observable<boolean> {
        if (this._disabledFeaturesProvider.isFeatureEnabled(FeatureCode.IOT_SUPERVISION)) {
            return combineLatest([
                this._profileProvider.hasInternalModuleAccess$(),
                this._profileProvider.hasRight$(IOT_SUPERVISION),
                this._applicationModulesProvider.hasModuleAccess$(ApplicationModuleCode.IOT_SUPERVISION)
            ]).pipe(
                map(([hasInternalModuleAccess, hasIoTSupervisionAccess, hasIoTSupervisionModuleAccess]) => {
                    return hasInternalModuleAccess || (hasIoTSupervisionAccess && hasIoTSupervisionModuleAccess);
                })
            );
        } else {
            return of(false);
        }
    }

    async hasFeatureAccess(): Promise<boolean> {
        return (
            (await this._profileProvider.hasRight(ACCESS_INTERNAL_MODULE)) ||
            ((await this._profileProvider.hasRight$(IOT_SUPERVISION)) &&
                (await this._applicationModulesProvider.hasModuleAccess(ApplicationModuleCode.IOT_SUPERVISION)))
        );
    }
}

export const ioTSupervisionGuard: CanActivateFn & CanActivateChildFn = async () => {
    const featureService = inject(IoTSupervisionFeatureService);
    const hasFeatureAccess = await featureService.hasFeatureAccess();

    if (!hasFeatureAccess) {
        // redirect ?
        return false;
    }
    return true;
};
